// Pure rules behind the Live Calls page (GET /telephony/supervision/live-channels),
// kept out of the component so they are directly testable.
//
// The server decides who sees which call (it attributes every channel to an
// organisation and filters to the caller's tree) and re-checks every action. This
// module only shapes what it returned: which buttons to show, the per-switch
// banner, the organisation filter for the platform view.

/** Roles the server admits to Live Calls (SupervisionController's @PreAuthorize). */
export const LIVE_CALL_ROLES = [
  'master_admin', 'platform_admin',
  'super_admin', 'provider_owner', 'service_admin', 'provider_admin',
  'operator_admin', 'business_admin',
];

export interface LiveSwitch {
  providerId: number;
  name: string;
  status: 'ok' | 'unreachable' | string;
  count: number;
  /** Whether listen/whisper can work on this switch at all. */
  listenAvailable?: boolean;
  /** Platform viewers only. */
  error?: string;
}

export interface LiveChannel {
  id: string;
  providerId: number;
  switchName?: string;
  name?: string | null;
  state?: string | null;
  direction?: 'inbound' | 'outbound' | 'internal' | 'unknown' | string | null;
  caller?: string | null;
  callerName?: string | null;
  dialed?: string | null;
  did?: string | null;
  startedAt?: string | null;
  durationSeconds?: number | null;
  endpoint?: string | null;
  peers?: string[];
  organizationId?: string | null;
  organizationName?: string | null;
  attributedBy?: 'DID' | 'ALIAS' | 'BILLING_PATTERN' | null;
  canSpy?: boolean;
  /** Why listen/whisper cannot work on this call for this viewer (switch without support, softphone elsewhere). */
  spyUnavailableReason?: string | null;
  canHangup?: boolean;
  /** Platform-only switch internals. */
  businessLabel?: string | null;
}

export interface LiveViewer {
  platform: boolean;
  organizationId?: string | null;
  canSpy: boolean;
  canHangup: boolean;
}

export interface LiveCallsData {
  switches: LiveSwitch[];
  channels: LiveChannel[];
  viewer: LiveViewer;
}

export const UNATTRIBUTED = '__unattributed__';
export const ALL = '__all__';

/** Normalises the API body (ApiResponse.data, or the body itself) into a safe shape. */
export function parseLiveCalls(body: any): LiveCallsData {
  const d = body?.data ?? body ?? {};
  const viewer = d.viewer || {};
  return {
    switches: Array.isArray(d.switches) ? d.switches : [],
    channels: Array.isArray(d.channels) ? d.channels : [],
    viewer: {
      platform: viewer.platform === true,
      organizationId: viewer.organizationId ?? null,
      canSpy: viewer.canSpy === true,
      canHangup: viewer.canHangup === true,
    },
  };
}

export function roleMayOpen(role: string | null | undefined): boolean {
  return LIVE_CALL_ROLES.includes((role || '').toLowerCase());
}

/** Which actions to SHOW on a row. The server re-checks each one regardless. */
export function actionsFor(ch: LiveChannel, viewer: LiveViewer): { spy: boolean; hangup: boolean; spyHint: string | null } {
  const spy = viewer.canSpy && ch.canSpy !== false;
  return {
    spy,
    hangup: viewer.canHangup && ch.canHangup !== false,
    // Instead of buttons that would fail: say why listening is not possible on this call.
    spyHint: viewer.canSpy && !spy ? (ch.spyUnavailableReason || 'Listening is not available on this call.') : null,
  };
}

/** The banner text for switches that did not answer, or null when all did. */
export function switchProblem(switches: LiveSwitch[]): string | null {
  const down = switches.filter(s => s.status !== 'ok');
  if (down.length === 0) return null;
  const names = down.map(s => s.name).join(', ');
  return down.length === switches.length
    ? `No switch answered (${names}) — the list below is empty because it could not be read, not because nothing is live.`
    : `${names} did not answer — calls on ${down.length === 1 ? 'it' : 'them'} are not shown.`;
}

/** Organisation filter options for the platform view: every org seen, plus Unattributed. */
export function orgOptions(channels: LiveChannel[]): { value: string; label: string; count: number }[] {
  const by = new Map<string, { label: string; count: number }>();
  for (const c of channels) {
    const key = c.organizationId || UNATTRIBUTED;
    const label = c.organizationId ? (c.organizationName || c.organizationId) : 'Unattributed';
    const cur = by.get(key) || { label, count: 0 };
    cur.count += 1;
    by.set(key, cur);
  }
  const opts = Array.from(by.entries()).map(([value, v]) => ({ value, label: v.label, count: v.count }));
  return opts.sort((a, b) =>
    a.value === UNATTRIBUTED ? 1 : b.value === UNATTRIBUTED ? -1 : a.label.localeCompare(b.label));
}

export function filterByOrg(channels: LiveChannel[], org: string): LiveChannel[] {
  if (!org || org === ALL) return channels;
  if (org === UNATTRIBUTED) return channels.filter(c => !c.organizationId);
  return channels.filter(c => c.organizationId === org);
}

/** Seconds the call has been up: the switch's figure, else from its start time. */
export function elapsedSeconds(ch: LiveChannel, now: number = Date.now()): number | null {
  if (typeof ch.durationSeconds === 'number' && ch.durationSeconds >= 0) return ch.durationSeconds;
  if (!ch.startedAt) return null;
  // ARI writes "2026-10-14T10:00:00.000+0530" — no colon in the offset, which
  // Safari's Date refuses. Insert it.
  const iso = String(ch.startedAt).replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((now - t) / 1000));
}

export function formatElapsed(sec: number | null): string {
  if (sec === null) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const mm = String(m).padStart(h ? 2 : 1, '0');
  const ss = String(s).padStart(2, '0');
  return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** Our number on the call, the one the call is attributed by. */
export function ourNumber(ch: LiveChannel): string | null {
  return ch.did || (ch.direction === 'outbound' ? ch.caller : ch.dialed) || null;
}

/** The other party. */
export function otherParty(ch: LiveChannel): string | null {
  return ch.direction === 'outbound' ? (ch.dialed || null) : (ch.caller || null);
}

/** Stable row key: channel ids are only unique per switch. */
export function rowKey(ch: LiveChannel): string {
  return `${ch.providerId}:${ch.id}`;
}
